import {
  alsoDrivesOf,
  effectiveRoleKey,
  formatCount,
  isCustomRoleKey,
  isRoleBase,
  roleBadgeColor,
  roleDisplayName,
  roleLabel as builtInRoleLabel,
  tt,
  type RoleBase,
  type RoleColor,
} from '@agos/shared'

import { roleKeyOf, type AssignRoleRequest, type RoleView } from '../roles/roles-api'

/**
 * Who works here, and what each of them can reach.
 *
 * **FB-099.** Legacy's role filter has four roles and the system has five. `roleFilterOptions` lists
 * *Všetky role*, `main_admin`, `admin`, `waiter`, `driver` — and no `cook`. There is one cook on the
 * staff, so that employee cannot be filtered for, and the counts on the chips do not add up: *Všetky*
 * says 23 while 2 + 6 + 3 + 11 is 22. One person is missing from every breakdown.
 *
 * Worse than a missing option: `cook` is the form's **default**.
 *
 * ```tsx
 * defaultValues: { name: "", position: "Obsluha", systemRole: "cook", … }
 * ```
 *
 * So an employee added without touching the role dropdown lands on the one role the filter cannot
 * select. The filter is built from the roles that exist here, which is one option per role by
 * construction, and the default is the role most of the staff actually hold.
 *
 * **FB-100.** `position` is free text with a default of `"Obsluha"`, and the data has drifted: 14
 * distinct positions across 23 people, including **`Šofer` six times and `Šofér` twice** — the same
 * job, two spellings — plus `veduci` and `Veduci`, and one employee whose position is the literal
 * string `waiter`, a system role typed into the wrong field. Nothing collapses them, so any report
 * grouped by position counts one job as two.
 */

export type SystemRole = 'main_admin' | 'admin' | 'cook' | 'waiter' | 'driver'

/** Every role the system has. Legacy's filter is missing the third one. */
export const SYSTEM_ROLES: readonly SystemRole[] = [
  'main_admin',
  'admin',
  'cook',
  'waiter',
  'driver',
]

// The thirteen fields this declared are a subset of the twenty-two the backend now publishes
// (BLOCKED #4), with the same nullability except `createdAt`, which this typed as nullable while the
// column is NOT NULL. Re-exported so the call sites in this module stay as they are and there is one
// definition rather than two that can drift.
import type { Employee } from '@agos/api-client/model'

export type { Employee }

/**
 * An employee being edited, which is not yet one the API answered.
 *
 * `Employee` gained a `required` list from its columns on 2026-08-03, so it means *a row that came
 * back* — eleven fields guaranteed. A form being filled in has none of them, and `employeeFormOf`
 * runs on exactly that: it is what turns a partial row into a complete form.
 */
export type EmployeeDraft = Partial<Employee>

export type BadgeColor = 'zinc' | 'blue' | 'emerald' | 'red' | 'amber' | 'violet'

const ROLE_COLORS: Record<SystemRole, BadgeColor> = {
  main_admin: 'violet',
  admin: 'blue',
  cook: 'emerald',
  waiter: 'emerald',
  driver: 'amber',
}

/** What each role reaches — legacy's own legend, kept because it is the only place it is written. */
const ROLE_ACCESS: Record<SystemRole, string[]> = {
  main_admin: [tt('Celý systém'), tt('Správa zamestnancov'), tt('Nastavenia'), tt('Prehľady')],
  admin: [tt('Objednávky'), tt('Kuchyňa'), tt('Rozvoz'), tt('Produkty'), tt('Štatistiky')],
  cook: [tt('Kuchyňa'), tt('Obedové menu')],
  waiter: [tt('Nová objednávka (reštaurácia)'), tt('Kuchyňa'), tt('Peňaženka')],
  driver: [tt('Rozvozová plocha'), tt('Peňaženka')],
}

/**
 * Who is looking at *Zamestnanci*, as the staff rules read them - `FRONTEND.md` §9.758, on `BACKEND.md` §10A.413.
 *
 * `hasPermission` is the auth context's: true for a main administrator, otherwise the person's own rights - so a
 * built-in Administrátor sees what their role ticks.
 */
export interface StaffReader {
  systemRole: string | null | undefined
  id?: number | null
  hasPermission: (key: string) => boolean
}

/** The three roles a holder of *Zamestnanci - Upraviť* manages - §10A.413's line staff. */
export const LINE_ROLES: readonly SystemRole[] = ['cook', 'waiter', 'driver']

/** Whether a `system_role` works as *Kuchár*, *Čašník* or *Šofér* - a custom role counts as its base. */
export function isLineRole(systemRole: string | null | undefined): boolean {
  return LINE_ROLES.some((role) => role === systemRole)
}

/**
 * Who may add and edit staff - `FRONTEND.md` §9.758 (owner: *"administrator cant create new employee"*), after
 * §9.742's O113 rule that only a main administrator may.
 *
 * The server's employee writes ask `employees_edit` since §10A.413, so *Pridať zamestnanca* and *Upraviť* follow the
 * right rather than the role; what a holder of it may do to whom is `staffEditLock`'s, and the server's
 * `LineStaffRules` say it again.
 */
export function mayManageStaff(reader: StaffReader): boolean {
  return reader.systemRole === 'main_admin' || reader.hasPermission('employees_edit')
}

/** Who may switch somebody on or off and remove them - `employees_delete`, §10A.413 rules 4 and 5. */
export function mayRemoveStaff(reader: StaffReader): boolean {
  return reader.systemRole === 'main_admin' || reader.hasPermission('employees_delete')
}

/** Why a row's edit, switch and delete are not offered to a holder of the right - §10A.413 rules 2 and 5. */
export type StaffLock = 'own-record' | 'not-line'

/**
 * Whether `employee` is out of the reader's reach: their own record (the profile edits it), or somebody whose role
 * is not *Kuchár*, *Čašník* or *Šofér* - an administrator or a main administrator. A main administrator is locked out
 * of nothing here; the older O113 rules (`deactivateRestriction`, `deleteRestriction`) still apply to them.
 */
export function staffEditLock(reader: StaffReader, employee: Employee): StaffLock | null {
  if (reader.systemRole === 'main_admin') return null
  if (employee.id !== undefined && reader.id !== undefined && reader.id !== null && employee.id === reader.id) {
    return 'own-record'
  }
  if (!isLineRole(employee.systemRole)) return 'not-line'
  return null
}

/** The row's tooltip for a lock - the server's own sentences (`LineStaffRules`), so the two say one thing. */
export function staffLockText(lock: StaffLock): string {
  if (lock === 'own-record') return tt('Vlastné údaje upravíte v profile.')
  return tt('Administrátora môže pridať alebo upraviť len hlavný administrátor.')
}

/**
 * The built-in roles a person may give - `FRONTEND.md` §9.742 and §9.758. A main administrator gives all five,
 * *Hlavný administrátor* included, which is the server's own rule (`BACKEND.md` §10A.394); anybody else the three line
 * roles (§10A.413 rule 1). A form already holding another role keeps it in the list, so opening a record never shows a
 * select with nothing chosen.
 */
export function rolesOfferedTo(reader: StaffReader, current: SystemRole): readonly SystemRole[] {
  if (reader.systemRole === 'main_admin') return SYSTEM_ROLES
  return SYSTEM_ROLES.filter((role) => isLineRole(role) || role === current)
}

export function isSystemRole(value: string | undefined): value is SystemRole {
  return (SYSTEM_ROLES as readonly string[]).includes(value ?? '')
}

/**
 * A role's label on this screen: the five built-ins by `@agos/shared`'s `roleLabel`, the one spelling of
 * them (O119, FRONTEND.md §9.753), and anything else as itself - blank for nothing, as this roster always
 * drew it.
 */
export function roleLabel(role: string | undefined): string {
  return isSystemRole(role) ? builtInRoleLabel(role) : (role ?? '')
}

export function roleColor(role: string | undefined): BadgeColor {
  return isSystemRole(role) ? ROLE_COLORS[role] : 'zinc'
}

export function roleAccess(role: SystemRole): string[] {
  return ROLE_ACCESS[role]
}

// ─── custom roles - O119, FRONTEND.md §9.753 ─────────────────────────────────

/**
 * The custom roles in an overview, in the order the roles screen draws them - O119, FRONTEND.md §9.753.
 *
 * **Only a custom role with a base it can work as.** `GET /settings/roles` (BACKEND.md §10A.408) lists the four
 * built-ins and, for a main administrator, `main_admin`'s read-only row beside the custom ones; those five are the
 * roster's own and are named by `roleLabel`, so this keeps a row that is `custom_<id>`, not built in, and based on one
 * of the four bases V89 allows. A row failing any of the three is not a role anybody can be moved into.
 *
 * **Absent is not empty** - a reader the overview refuses (anybody who neither is a main administrator nor manages a
 * role), a read that failed and one still in flight all give nothing here, and the roster then names a custom role
 * by its number rather than by a name it cannot see (`rosterRoleOf`).
 */
export function customRolesOf(roles: readonly RoleView[] | null | undefined): RoleView[] {
  return (roles ?? [])
    .filter((role) => !role.builtIn && isCustomRoleKey(role.key) && isRoleBase(role.baseRole))
    .sort(
      (left, right) =>
        left.sortOrder - right.sortOrder ||
        roleDisplayName(left).localeCompare(roleDisplayName(right), 'sk'),
    )
}

/**
 * The role a person holds - `COALESCE(role_key, system_role)`, the rule the server decides by (§10A.407).
 *
 * `GET /employees` answers `roleKey` since §10A.408 and the generated `Employee` learns it at the next regeneration,
 * so it is read off the untyped row (`roleKeyOf`); a server from before the deploy answers none, which is a built-in
 * role - every person's today.
 */
export function rosterRoleKey(employee: Employee): string {
  return effectiveRoleKey({ systemRole: employee.systemRole, roleKey: roleKeyOf(employee) })
}

/**
 * A custom role the reader cannot see the name of: its number and what it works as.
 *
 * **Not the key.** §9.257 is this roster's own lesson - the sidebar printed `main_admin` under every name - and
 * `custom_3` is the same defect. The base is what an administrator without the roles screen can act on (it is what
 * every role-listed call decides by), and the number keeps two such roles apart in the filter.
 */
function unseenCustomRoleLabel(key: string, base: string | null | undefined): string {
  const number = key.slice('custom_'.length)
  const worksAs = isSystemRole(base ?? undefined) ? builtInRoleLabel(base) : ''
  const named = `${tt('Vlastná rola')} #${number}`
  return worksAs === '' ? named : `${named} · ${worksAs}`
}

/** How the roster draws a person's role: its badge, its colour and, for a custom role, what it works as. */
export interface RosterRole {
  /** `COALESCE(role_key, system_role)` - what the filter groups by. */
  key: string
  label: string
  color: RoleColor
  /** *Pracuje ako: Čašník* for a custom role, said in the badge's title; null for a built-in. */
  worksAs: string | null
}

/**
 * A person's role as the *Rola* column draws it - O119, FRONTEND.md §9.753.
 *
 * - a built-in role keeps its label and its colour, exactly as before;
 * - a custom role the reader can see is drawn by its own name (`roleDisplayName`) in its own colour, falling back to
 *   its base's colour when it has none (V89 allows null) or one the badge does not know;
 * - a custom role the reader cannot see is drawn by `unseenCustomRoleLabel` in its base's colour.
 *
 * Either kind of custom role says what it works as in the title, because the base is what the server decides by.
 */
export function rosterRoleOf(employee: Employee, roles: readonly RoleView[] | null | undefined): RosterRole {
  const key = rosterRoleKey(employee)
  if (!isCustomRoleKey(key)) return { key, label: roleLabel(key), color: roleColor(key), worksAs: null }

  const base = employee.systemRole
  const role = customRolesOf(roles).find((candidate) => candidate.key === key)
  return {
    key,
    label: role === undefined ? unseenCustomRoleLabel(key, base) : roleDisplayName(role),
    color: roleBadgeColor(role?.color, roleColor(base)),
    worksAs: `${tt('Pracuje ako')}: ${roleLabel(base)}`,
  }
}

/** A custom role the role select offers, with the base the person would work as. */
export interface CustomRoleChoice {
  key: string
  label: string
  base: RoleBase
}

/**
 * The custom roles *Rola v systéme* offers under *Vlastné roly* - FRONTEND.md §9.753.
 *
 * **The person's own custom role stays in the list** when the reader cannot see it (the overview failed or has not
 * answered yet), for the reason `rolesOfferedTo` keeps *Hlavný administrátor* for a record that holds it: a select
 * whose value matches no option draws nothing chosen, and a save would then look like a change of role.
 */
export function customRoleChoices(
  roles: readonly RoleView[] | null | undefined,
  held: { roleKey: string; systemRole: string } | null,
  reader?: StaffReader,
): CustomRoleChoice[] {
  const choices: CustomRoleChoice[] = []
  // §9.758 - anybody but a main administrator is offered the custom roles they may assign on a line base: the
  // assignment asks `may_assign` on the role (§10A.408), and a role on Administrátor is never theirs to give.
  const onlyAssignable = reader !== undefined && reader.systemRole !== 'main_admin'
  for (const role of customRolesOf(roles)) {
    if (onlyAssignable && (!role.you.mayAssign || !isLineRole(role.baseRole))) continue
    if (isRoleBase(role.baseRole)) {
      choices.push({ key: role.key, label: roleDisplayName(role), base: role.baseRole })
    }
  }
  if (
    held !== null &&
    isCustomRoleKey(held.roleKey) &&
    isRoleBase(held.systemRole) &&
    !choices.some((choice) => choice.key === held.roleKey)
  ) {
    choices.push({
      key: held.roleKey,
      label: unseenCustomRoleLabel(held.roleKey, held.systemRole),
      base: held.systemRole,
    })
  }
  return choices
}

/**
 * The form after a role is picked: the key, and the base the person will work as.
 *
 * A built-in role is its own base. A custom role takes its base from the choice, so the switches that follow the
 * base - *Externý vodič* for a driver, *Jazdí aj rozvoz* for everybody else - follow it too. A key the select does
 * not offer leaves the form as it was.
 */
export function chooseRole(
  form: EmployeeForm,
  key: string,
  custom: readonly CustomRoleChoice[],
): EmployeeForm {
  if (isSystemRole(key)) return { ...form, roleKey: key, systemRole: key }
  const choice = custom.find((candidate) => candidate.key === key)
  return choice === undefined ? form : { ...form, roleKey: choice.key, systemRole: choice.base }
}

/**
 * Whether saving this form changes the person's base - and so signs them out on every device (§10A.406's trigger:
 * the token carries the base, and a change of it retires every token issued before). A move between two roles with
 * one base does not, and a new person has nothing to sign out of.
 */
export function roleChangeSignsOut(form: EmployeeForm, record: Employee | undefined): boolean {
  return record !== undefined && form.systemRole !== (record.systemRole ?? '')
}

/** *Zmena roly odhlási {meno} na všetkých zariadeniach.* - the sentence the dialog says before the save. */
export function signOutWarning(name: string | null | undefined): string {
  const who = name?.trim() ?? ''
  return tt('Zmena roly odhlási {meno} na všetkých zariadeniach.').replace(
    '{meno}',
    who === '' ? tt('tohto človeka') : who,
  )
}

/**
 * The line under *Rola v systéme*: legacy's reach for a built-in role, and for a custom role what it works as and
 * the description its main administrator wrote - its rights are its own, so the built-in's reach would describe
 * somebody else.
 */
export function roleNote(form: EmployeeForm, roles: readonly RoleView[] | null | undefined): string {
  if (!isCustomRoleKey(form.roleKey)) return roleAccess(form.systemRole).join(' · ')
  const worksAs = `${tt('Pracuje ako')}: ${roleLabel(form.systemRole)}`
  const role = customRolesOf(roles).find((candidate) => candidate.key === form.roleKey)
  const description = role?.description?.trim() ?? ''
  return description === '' ? worksAs : `${worksAs} · ${description}`
}

/**
 * What a save sends - FRONTEND.md §9.753.
 *
 * **A change of role that involves a custom role goes through `PUT /employees/{id}/role`**, the assignment BACKEND.md
 * §10A.408 writes (`system_role` = the base, `role_key` = the custom key or null), and the other fields through `PATCH`
 * as before - **without** `systemRole`, so the two writes cannot disagree about the role. Only the assignment can set
 * or clear `role_key`: a `PATCH` naming the base a custom role already works as changes nothing, so moving somebody out
 * of a custom role into its own base's built-in is the assignment too.
 *
 * **A move between two built-in roles stays the `PATCH` alone, `systemRole` in it, exactly as it always was.** The
 * assignment is the `permissions` module's (`@RequireModule("permissions")`, §10A.408) and the `employees` screen is
 * not: a franchise whose package lacks the module - the shipped `basic` tier - must still be able to move a waiter to
 * the kitchen, and the old `PATCH` has no module gate. V88's trigger signs the person out on a change of base whichever
 * write makes it. A save that keeps the role is the `PATCH` alone too: the same value written again changes nothing,
 * and V89's trigger keeps a custom role whose base is unchanged.
 *
 * **Creating somebody into a custom role is a `POST` with the base, then the `PUT`**, because the employee endpoints
 * take one of the five roles and nothing else. A built-in role is the `POST` alone.
 *
 * `expectedRoleKey` is the role as read - for a new person, the base the `POST` gave them - so somebody else's move in
 * between is refused (`ROLE_STALE`) rather than overwritten.
 *
 * **The assignment goes first, so a refused move leaves everything as it was - except when it ends the editor's own
 * session.** A main administrator changing their own base is signed out by that write (§10A.406's trigger bumps the
 * token every request carries), so a `PATCH` sent after it would be refused and the other fields lost with nobody told.
 * Then the `PATCH` goes first and the move last (`assignLast`): the write that ends the session is the one with
 * nothing after it.
 */
export type EmployeeSave =
  | { kind: 'create'; payload: Record<string, unknown>; assign: AssignRoleRequest | null }
  | {
      kind: 'update'
      id: number
      payload: Record<string, unknown>
      assign: AssignRoleRequest | null
      assignLast: boolean
    }

export function employeeSave(
  editingId: number | null,
  form: EmployeeForm,
  heldRoleKey: string | null,
  endsOwnSession = false,
  everyMoveAssigns = false,
): EmployeeSave {
  const payload = employeePayload(form)
  if (editingId === null) {
    return {
      kind: 'create',
      payload,
      assign: isCustomRoleKey(form.roleKey)
        ? { roleKey: form.roleKey, expectedRoleKey: form.systemRole }
        : null,
    }
  }
  // §10A.413 rule 3 - for anybody but a main administrator every change of role is the assignment: the `PATCH` refuses
  // one (`STAFF_ROLE_VIA_ASSIGNMENT`), and `employees_edit` is the assignment's authority between the line roles.
  if (
    form.roleKey === heldRoleKey ||
    (!everyMoveAssigns && !movesThroughTheAssignment(heldRoleKey, form.roleKey))
  ) {
    return { kind: 'update', id: editingId, payload, assign: null, assignLast: false }
  }
  delete payload.systemRole
  return {
    kind: 'update',
    id: editingId,
    payload,
    assign: { roleKey: form.roleKey, expectedRoleKey: heldRoleKey },
    assignLast: endsOwnSession,
  }
}

/**
 * Whether a move needs the assignment (`PUT /employees/{id}/role`) rather than the old `PATCH`: exactly when a custom
 * role is on either side, which is the only move the `PATCH` cannot make (it cannot set or clear `role_key`) and the
 * only one a franchise without the `permissions` module cannot have reached in the first place.
 */
export function movesThroughTheAssignment(heldRoleKey: string | null, nextRoleKey: string): boolean {
  return isCustomRoleKey(heldRoleKey) || isCustomRoleKey(nextRoleKey)
}

/**
 * Whether the reader may open *Role a práva* - the legend's link (FRONTEND.md §9.753).
 *
 * The route's own rule (`managerHrModule`'s `/permissions` guard): a main administrator, or somebody whose role
 * manages another (`managesRoles`, off `/auth/me`) - and either only while the franchise's tier has the `permissions`
 * module, which the guard keeps for both. A link to a page that then refuses is §9.7's defect, so the link asks what
 * the route asks; the shell's guard test holds the two equal.
 */
export function mayOpenRolesScreen(reader: {
  systemRole: string | null | undefined
  managesRoles: boolean
  permissionsModule: boolean
}): boolean {
  if (!reader.permissionsModule) return false
  return reader.systemRole === 'main_admin' || reader.managesRoles
}

/** Initials for the avatar fallback — two at most, as legacy does. */
export function initialsOf(name: string | undefined): string {
  return (name ?? '')
    .split(/\s+/)
    .filter((word) => word !== '')
    .map((word) => word[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

// ─── the list ────────────────────────────────────────────────────────────────

export interface RosterFilter {
  tab: 'active' | 'inactive'
  role: string
  search: string
}

export function matchesSearch(employee: Employee, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (needle === '') return true
  return [employee.name, employee.email, employee.phone, employee.position].some((field) =>
    String(field ?? '').toLowerCase().includes(needle),
  )
}

export function filterRoster(employees: Employee[], filter: RosterFilter): Employee[] {
  return employees.filter((employee) => {
    const active = employee.active !== false
    if (filter.tab === 'active' ? !active : active) return false
    // O119 - by the role a person holds, so *Čašník* lists the built-in waiters and a custom role based on it lists
    // its own members: the two options the filter counts separately (`roleOptions`).
    if (filter.role !== 'all' && rosterRoleKey(employee) !== filter.role) return false
    return matchesSearch(employee, filter.search)
  })
}

export interface RoleOption {
  value: string
  label: string
  count: number
}

/**
 * One filter option per role that exists on the staff, plus *all* — **FB-099**.
 *
 * Built from the data rather than from a hand-written list, so the option cannot go missing and the
 * counts add up to the total by construction. A role nobody holds is not offered, because a filter
 * that always returns nothing is noise.
 *
 * **O119 (FRONTEND.md §9.753): one option per role a person holds**, `COALESCE(role_key, system_role)` - the five
 * built-ins first, then the custom roles in the overview's order, then whatever else is held: a custom role the reader
 * cannot see, by its number, and a role the enum does not know, as itself. Each person is counted under exactly one
 * key, which is what keeps FB-099's sum: a custom role based on *Čašník* is its own option and not part of *Čašník*'s.
 */
export function roleOptions(
  employees: Employee[],
  tab: 'active' | 'inactive',
  roles: readonly RoleView[] | null | undefined = [],
): RoleOption[] {
  const pool = employees.filter((employee) =>
    tab === 'active' ? employee.active !== false : employee.active === false,
  )
  const held = new Map<string, { count: number; first: Employee }>()
  for (const employee of pool) {
    const key = rosterRoleKey(employee)
    const entry = held.get(key)
    if (entry === undefined) held.set(key, { count: 1, first: employee })
    else entry.count += 1
  }

  const options: RoleOption[] = [{ value: 'all', label: tt('Všetky role'), count: pool.length }]
  const offered = new Set<string>()
  const offer = (key: string, label: string): void => {
    const entry = held.get(key)
    if (entry === undefined || offered.has(key)) return
    offered.add(key)
    options.push({ value: key, label, count: entry.count })
  }
  for (const role of SYSTEM_ROLES) offer(role, roleLabel(role))
  for (const role of customRolesOf(roles)) offer(role.key, roleDisplayName(role))
  // A role the enum does not know about, or a custom role the reader cannot see, would otherwise vanish from the
  // breakdown entirely.
  for (const [key, entry] of held) {
    if (key !== '') offer(key, rosterRoleOf(entry.first, roles).label)
  }
  return options
}

/** Whether the options account for everybody — the invariant legacy's hand-written list broke. */
export function optionsAddUp(options: RoleOption[]): boolean {
  const all = options.find((option) => option.value === 'all')?.count ?? 0
  const rest = options
    .filter((option) => option.value !== 'all')
    .reduce((total, option) => total + option.count, 0)
  return all === rest
}

export interface RosterTotals {
  total: number
  active: number
  inactive: number
  external: number
  withCard: number
}

export function rosterTotals(employees: Employee[]): RosterTotals {
  let active = 0
  let external = 0
  let withCard = 0
  for (const employee of employees) {
    if (employee.active !== false) active += 1
    if (employee.isExternal === true) external += 1
    if ((employee.hikvisionCardNo ?? '') !== '') withCard += 1
  }
  return {
    total: employees.length,
    active,
    inactive: employees.length - active,
    external,
    withCard,
  }
}

// ─── positions, and the drift in them — FB-100 ───────────────────────────────

/**
 * A position reduced to what it means: lower case, no diacritics, single-spaced.
 *
 * `Šofer` and `Šofér` are the same job. So are `veduci` and `Veduci`. Compared this way so the page
 * can say which of the fourteen positions in the data are duplicates of each other.
 */
export function normalisePosition(position: string | undefined): string {
  return (position ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .replace(/\s+/g, ' ')
}

export interface PositionGroup {
  /** The spelling most people share; ties go to the first alphabetically, for stability. */
  canonical: string
  spellings: string[]
  people: number
}

/** The positions in use, grouped by what they mean rather than by how they were typed. */
export function positionGroups(employees: Employee[]): PositionGroup[] {
  const groups = new Map<string, Map<string, number>>()
  for (const employee of employees) {
    const raw = (employee.position ?? '').trim()
    if (raw === '') continue
    const key = normalisePosition(raw)
    const spellings = groups.get(key) ?? new Map<string, number>()
    spellings.set(raw, (spellings.get(raw) ?? 0) + 1)
    groups.set(key, spellings)
  }

  return [...groups.values()]
    .map((spellings) => {
      const entries = [...spellings.entries()].sort(
        (left, right) => right[1] - left[1] || left[0].localeCompare(right[0]),
      )
      return {
        canonical: entries[0]?.[0] ?? '',
        spellings: entries.map(([spelling]) => spelling),
        people: entries.reduce((total, [, count]) => total + count, 0),
      }
    })
    .sort((left, right) => right.people - left.people || left.canonical.localeCompare(right.canonical))
}

/** Groups written more than one way — the ones a report grouped by position miscounts. */
export function driftedPositions(employees: Employee[]): PositionGroup[] {
  return positionGroups(employees).filter((group) => group.spellings.length > 1)
}

/** A position that is really a system role somebody typed into the wrong field. */
export function positionsThatAreRoles(employees: Employee[]): string[] {
  const found = new Set<string>()
  for (const employee of employees) {
    const raw = (employee.position ?? '').trim()
    if (raw !== '' && isSystemRole(raw.toLowerCase())) found.add(raw)
  }
  return [...found]
}

/** The spellings already in use, so the next one matches instead of adding a fifteenth. */
export function positionSuggestions(employees: Employee[]): string[] {
  return positionGroups(employees).map((group) => group.canonical)
}

/**
 * The spelling in use that this one means the same as, when they are written differently.
 *
 * Silent when the input *is* the canonical spelling, and silent when the position is new. It answers
 * on `Šofér` — already in use twice — because the commonest spelling is `Šofer`, and one job written
 * two ways is the drift FB-100 is about.
 */
export function duplicatesExistingPosition(
  position: string,
  employees: Employee[],
): string | null {
  const key = normalisePosition(position)
  if (key === '') return null
  const group = positionGroups(employees).find(
    (candidate) => normalisePosition(candidate.canonical) === key,
  )
  if (group === undefined) return null
  return group.canonical === position.trim() ? null : group.canonical
}

// ─── the form ────────────────────────────────────────────────────────────────

export interface EmployeeForm {
  name: string
  position: string
  /**
   * The role picked in *Rola v systéme* - a built-in key or `custom_<id>` (O119, FRONTEND.md §9.753). For a built-in it
   * equals `systemRole`; for a custom role `systemRole` is the base it works as, which is what the employee endpoints
   * take and what every switch below follows.
   */
  roleKey: string
  /** The base: one of the five, and the role itself unless `roleKey` names a custom one. */
  systemRole: SystemRole
  phone: string
  email: string
  password: string
  active: boolean
  showWallet: boolean
  /**
   * A driver hired through an agency rather than on the payroll — §9.2.
   *
   * Only meaningful for a driver: it decides whether the per-delivery reward is computed at all, and
   * `/external-drivers` is the screen that pays them. Every other role ignores it.
   */
  isExternal: boolean
  /**
   * *Jazdí aj rozvoz* - §9.685, owner #25.
   *
   * An account of any other role that also delivers: offered on the dispatch board, sendable with *Vyraziť*, its
   * deliveries counted in its wallet like a driver's. Meaningless for a driver, who drives by role.
   */
  alsoDrives: boolean
  hikvisionCardNo: string
}

/**
 * A new employee starts as whatever most of the staff are.
 *
 * Legacy hardcodes `cook`, which is the one role its own filter cannot select (FB-099) and the
 * rarest on the staff — one person out of 23.
 */
export function commonestRole(employees: Employee[]): SystemRole {
  const counts = new Map<SystemRole, number>()
  for (const employee of employees) {
    if (isSystemRole(employee.systemRole)) {
      counts.set(employee.systemRole, (counts.get(employee.systemRole) ?? 0) + 1)
    }
  }
  let best: SystemRole = 'waiter'
  let bestCount = -1
  for (const role of SYSTEM_ROLES) {
    // Never default somebody into an administrator.
    if (role === 'main_admin' || role === 'admin') continue
    const count = counts.get(role) ?? 0
    if (count > bestCount) {
      best = role
      bestCount = count
    }
  }
  return best
}

export function emptyEmployeeForm(employees: Employee[]): EmployeeForm {
  const role = commonestRole(employees)
  return {
    name: '',
    position: '',
    roleKey: role,
    systemRole: role,
    phone: '',
    email: '',
    password: '',
    active: true,
    showWallet: true,
    isExternal: false,
    alsoDrives: false,
    hikvisionCardNo: '',
  }
}

export function employeeFormOf(employee: EmployeeDraft): EmployeeForm {
  const systemRole = isSystemRole(employee.systemRole) ? employee.systemRole : 'waiter'
  // O119 - a custom role is kept only over a base the form can hold; the row's key is read as the server sent it.
  const custom = 'roleKey' in employee ? employee.roleKey : null
  return {
    name: employee.name ?? '',
    position: employee.position ?? '',
    roleKey:
      typeof custom === 'string' && isCustomRoleKey(custom) && systemRole === employee.systemRole
        ? custom
        : systemRole,
    systemRole,
    phone: employee.phone ?? '',
    email: employee.email ?? '',
    password: '',
    active: employee.active !== false,
    showWallet: employee.showWallet !== false,
    isExternal: employee.isExternal === true,
    // Off the untyped row: the generated `Employee` learns the field at the next regeneration (§10A.343).
    alsoDrives: alsoDrivesOf(employee),
    hikvisionCardNo: employee.hikvisionCardNo ?? '',
  }
}

export type EmployeeProblem =
  | { kind: 'no-name' }
  | { kind: 'name-too-short' }
  | { kind: 'no-position' }
  | { kind: 'position-is-role'; value: string }
  | { kind: 'position-duplicate'; existing: string }
  | { kind: 'bad-email'; value: string }
  | { kind: 'email-taken'; name: string }
  | { kind: 'password-too-short' }
  | { kind: 'no-credentials' }

/** Legacy's own minimum, kept. */
const MIN_PASSWORD = 6
const MIN_NAME = 2

/** Deliberately loose — the server is the authority, and a valid address can look odd. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export function employeeProblems(
  form: EmployeeForm,
  employees: Employee[],
  editingId: number | null,
): EmployeeProblem[] {
  const problems: EmployeeProblem[] = []
  const name = form.name.trim()
  if (name === '') problems.push({ kind: 'no-name' })
  else if (name.length < MIN_NAME) problems.push({ kind: 'name-too-short' })

  const position = form.position.trim()
  if (position === '') problems.push({ kind: 'no-position' })
  else if (isSystemRole(position.toLowerCase())) {
    // One employee in the data has `position = 'waiter'` (FB-100).
    problems.push({ kind: 'position-is-role', value: position })
  } else {
    const others = employees.filter((employee) => employee.id !== editingId)
    const duplicate = duplicatesExistingPosition(position, others)
    if (duplicate !== null) problems.push({ kind: 'position-duplicate', existing: duplicate })
  }

  const email = form.email.trim()
  if (email !== '') {
    if (!EMAIL_PATTERN.test(email)) problems.push({ kind: 'bad-email', value: email })
    else {
      const clash = employees.find(
        (employee) =>
          employee.id !== editingId &&
          (employee.email ?? '').trim().toLowerCase() === email.toLowerCase(),
      )
      if (clash !== undefined) problems.push({ kind: 'email-taken', name: clash.name ?? '' })
    }
  }

  const password = form.password.trim()
  if (password !== '' && password.length < MIN_PASSWORD) {
    problems.push({ kind: 'password-too-short' })
  }
  // An employee with no address and no password cannot sign in — legacy allows it silently.
  if (editingId === null && email === '' && password === '') {
    problems.push({ kind: 'no-credentials' })
  }

  return problems
}

/** The duplicate-position and no-credentials warnings do not block a save. */
export function canSaveEmployee(problems: EmployeeProblem[]): boolean {
  return !problems.some(
    (problem) =>
      problem.kind === 'no-name' ||
      problem.kind === 'name-too-short' ||
      problem.kind === 'no-position' ||
      problem.kind === 'position-is-role' ||
      problem.kind === 'bad-email' ||
      problem.kind === 'email-taken' ||
      problem.kind === 'password-too-short',
  )
}

/** An untouched password box sends nothing, so saving a name cannot clear a credential. */
export function employeePayload(form: EmployeeForm): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: form.name.trim(),
    position: form.position.trim(),
    systemRole: form.systemRole,
    phone: form.phone.trim() || null,
    email: form.email.trim() || null,
    active: form.active,
    showWallet: form.showWallet,
    // Sent only for a driver. On any other role the flag is meaningless, and writing `false` to every
    // cook and waiter would put a column nobody reads into every audit row of every save.
    // §9.685, owner #25 - *Jazdí aj rozvoz* is the mirror: sent for every role but a driver, who drives by role and
    // for whom the switch would say nothing.
    ...(form.systemRole === 'driver' ? { isExternal: form.isExternal } : { alsoDrives: form.alsoDrives }),
    hikvisionCardNo: form.hikvisionCardNo.trim() || null,
  }
  const password = form.password.trim()
  if (password !== '') payload.password = password
  return payload
}

// ─── what may be done to whom ────────────────────────────────────────────────

export type Restriction = 'own-account' | 'last-main-admin' | 'main-admin'

/**
 * Why an action is not offered.
 *
 * Legacy checks two of these and only after the click, as a toast: deactivating a `main_admin` and
 * deleting yourself. It does not check the third, which is the one that locks everybody out — the
 * *last* main administrator. There are two in this data, one of them a developer account.
 */
export function deactivateRestriction(
  employee: Employee,
  employees: Employee[],
): Restriction | null {
  if (employee.systemRole === 'main_admin') {
    const remaining = employees.filter(
      (candidate) =>
        candidate.systemRole === 'main_admin' &&
        candidate.active !== false &&
        candidate.id !== employee.id,
    ).length
    return remaining === 0 ? 'last-main-admin' : 'main-admin'
  }
  return null
}

export function deleteRestriction(
  employee: Employee,
  employees: Employee[],
  currentUserId: number | undefined,
): Restriction | null {
  if (employee.id !== undefined && employee.id === currentUserId) return 'own-account'
  if (employee.systemRole === 'main_admin') {
    const remaining = employees.filter(
      (candidate) => candidate.systemRole === 'main_admin' && candidate.id !== employee.id,
    ).length
    if (remaining === 0) return 'last-main-admin'
  }
  return null
}

export function restrictionText(restriction: Restriction): string {
  if (restriction === 'own-account') return tt('Vlastný účet zmazať nemôžete.')
  if (restriction === 'last-main-admin') {
    return tt('Toto je posledný hlavný administrátor - bez neho sa do systému nikto nedostane.')
  }
  return tt('Hlavného administrátora sa deaktivovať nedá.')
}

/**
 * Whether demoting this employee would leave nobody able to administer the system.
 *
 * The role dropdown lets a main administrator make themselves a driver. Nothing in legacy stops it,
 * and the next screen they see is the driver surface.
 */
export function demotionLocksEveryoneOut(
  form: EmployeeForm,
  employee: Employee,
  employees: Employee[],
): boolean {
  if (employee.systemRole !== 'main_admin' || form.systemRole === 'main_admin') return false
  return (
    employees.filter(
      (candidate) =>
        candidate.systemRole === 'main_admin' &&
        candidate.active !== false &&
        candidate.id !== employee.id,
    ).length === 0
  )
}

export { formatCount }
